import type { IVector3 } from '@seddi/math-v2';
import { type TTexture, SurfaceMaterialModel } from '@seddi/surface-material';
import {
  type LoadingManager,
  type Texture,
  type WebGLRenderer,
  CustomBlending,
  MeshPhysicalMaterial,
  OneFactor,
  RepeatWrapping,
  RGBAFormat,
} from '@seddi/three';
import type { RecolorContext } from '@seddi/ui-recolor';
import { produce } from 'immer';
import log from 'loglevel';
import * as FlagsModule from '../flags/flagsModule';
import { FeatFlag } from '../flags/flagTypes';
import type { SurfaceMaterialDesc } from './materialTypes';
import { load as loadTexure } from './textureLoader';
import { generateCustomMipMaps } from './textureMipmaps';
import { textureMapsKeys } from './textureTypes';

export const ParamsMapDisney = {
  alphaMap: 'alphaMap',
  baseColor: 'color',
  baseColorMap: 'map',
  displacementMap: 'heightMap',
  metallic: 'metalness',
  metallicMap: 'metalnessMap',
  normalMap: 'normalMap',
  roughness: 'roughness',
  roughnessMap: 'roughnessMap',
  specular: 'specularIntensity',
  specularMap: 'specularIntensityMap',
};

const ParamsMap = ParamsMapDisney;

/**
 * Load material.
 * @param desc - Material.
 * @param manager - Loading manager.
 * @param renderer - WebGL renderer.
 */
export const load = async (
  desc: SurfaceMaterialDesc,
  manager: LoadingManager,
  renderer: WebGLRenderer,
): Promise<MeshPhysicalMaterial | undefined> => {
  const { material, textures } = load1st(desc);

  // load textures
  const textureTags = desc.tags;

  const promises = textures.map(async ({ key, value: textureDesc }) => {
    let texture;
    switch (key) {
      case 'baseColorMap':
      case 'normalMap': {
        // compute custom mipmap for albedo and normal maps
        texture = await loadTextureMipMaps(textureDesc, manager, renderer, textureTags);
        break;
      }
      default: {
        texture = await loadTexure(textureDesc, manager, renderer);
        break;
      }
    }
    material[ParamsMap[key]] = texture;
  });
  await Promise.all(promises);

  load2nd(desc, material);

  return material;
};

/**
 * Load material with albedo recolor.
 * @param desc - Material.
 * @param color - Target color.
 * @param manager - Loading manager.
 * @param renderer - WebGL renderer.
 * @param recolor - Recolor context.
 */
export const loadRecolor = async (
  desc: SurfaceMaterialDesc,
  manager: LoadingManager,
  renderer: WebGLRenderer,
  color: IVector3,
  recolor: RecolorContext,
): Promise<MeshPhysicalMaterial | undefined> => {
  const { material, textures } = load1st(desc);

  // load textures
  const textureTags = desc.tags;

  const promises = textures.map(async ({ key, value: textureDesc }) => {
    let texture;
    switch (key) {
      case 'baseColorMap': {
        // compute custom mipmap for albedo and normal maps
        texture = await loadTextureRecolorMipMaps(
          textureDesc,
          manager,
          renderer,
          color,
          recolor,
          textureTags,
        );
        break;
      }
      case 'normalMap': {
        // compute custom mipmap for albedo and normal maps
        texture = await loadTextureMipMaps(textureDesc, manager, renderer, textureTags);
        break;
      }
      default: {
        texture = await loadTexure(textureDesc, manager, renderer);
        break;
      }
    }
    // Compute custom mipmap levels for the albedo and normal maps.
    material[ParamsMap[key]] = texture;
  });
  await Promise.all(promises);

  load2nd(desc, material);

  return material;
};

const load1st = (desc: SurfaceMaterialDesc) => {
  // only support Disney 2012
  if (desc.model !== SurfaceMaterialModel.DISNEY) {
    log.warn(`Unsuported shading model ${desc.id}`);
  }

  const material = new MeshPhysicalMaterial({});
  const textures: { key: string; value: TTexture }[] = [];

  Object.keys(desc.params).forEach((key) => {
    switch (key) {
      case 'baseColor': {
        const value = desc.params[key];
        material[ParamsMap[key]].setRGB(value.x, value.y, value.z);
        break;
      }
      case 'metallic':
      case 'roughness':
      case 'specular': {
        const value = desc.params[key];
        material[ParamsMap[key]] = value;
        break;
      }
      case 'alphaMap':
      case 'baseColorMap':
      case 'metallicMap':
      case 'normalMap':
      case 'roughnessMap':
      case 'specularMap': {
        const value = desc.params[key] as TTexture;
        textures.push({ key, value });
        break;
      }
      case 'displacementMap': {
        if (FlagsModule.isEnabled(FeatFlag.PARALLAX)) {
          const value = desc.params[key] as TTexture;
          textures.push({ key, value });
        }
        break;
      }
      default:
        break;
    }
  });

  // TODO: Alpha included in baseColorMap
  if (desc.params.alphaMap) {
    material.transparent = true;
  }

  if (desc.tags?.includes('FABRIC')) {
    material.userData = { fabric: true };
    material.alphaTest = 0.04;
    material.defines.USE_SPECULARITY_BACKLIGHT_COMPENSATION = '1';

    if (!FlagsModule.isEnabled(FeatFlag.DISABLE_TRANSPARENCY_ANGLE_BASED_COMPENSATION)) {
      material.defines.USE_TRANSPARENCY_ANGLE_BASED_COMPENSATION = '1';
    }
    if (!FlagsModule.isEnabled(FeatFlag.DISABLE_TRANSPARENCY_FLAT_COMPENSATION)) {
      material.defines.USE_TRANSPARENCY_FLAT_COMPENSATION = '1';
    }
    if (FlagsModule.isEnabled(FeatFlag.PARALLAX) && desc.params.displacementMap) {
      // @ts-expect-error: Parallax properties
      material.parallaxActive = true;
      const fabricThicknessTag = desc.tags?.find((t) => t.includes('FABRIC_THICKNESS_')) || '';
      let fabricThickness = 0;
      if (fabricThicknessTag) {
        fabricThickness = parseFloat(fabricThicknessTag.replace('FABRIC_THICKNESS_', ''));
      }
      // @ts-expect-error: Parallax properties
      material.parallaxScale = Math.min(fabricThickness / 1000, 0.003);
    }
  }

  if (desc.tags?.includes('LOGO')) {
    material.alphaTest = 0.04;
    material.defines.UV_NOREPEAT = '1';
  }

  return { material, textures };
};

const load2nd = (desc: SurfaceMaterialDesc, material: MeshPhysicalMaterial) => {
  // alpha map inside base color
  if (material.map?.format === RGBAFormat) {
    material.transparent = true;
  }

  // repeat
  const { repeat } = desc;
  if (repeat) {
    material.alphaMap?.repeat.copy(repeat);
    material.map?.repeat.copy(repeat);
    material.metalnessMap?.repeat.copy(repeat);
    material.normalMap?.repeat.copy(repeat);
    material.roughnessMap?.repeat.copy(repeat);
    material.specularIntensityMap?.repeat.copy(repeat);
  }

  if (desc.tags?.includes('FABRIC')) {
    // enable repeat wrapping on textures
    for (const key of textureMapsKeys) {
      const texture = material[key] as Texture;
      if (!texture) {
        continue;
      }
      texture.wrapS = RepeatWrapping;
      texture.wrapT = RepeatWrapping;
    }
  }

  // enable premultiplied alpha blending equation
  const usePremultipliedAlpha =
    FlagsModule.isEnabled(FeatFlag.PREMULTIPLIED_ALPHA_LOGOS) && desc.tags?.includes('LOGO');
  if (usePremultipliedAlpha) {
    if (material.transparent) {
      material.blending = CustomBlending;
      material.blendSrc = OneFactor;
    }
  }
};

const loadTextureMipMaps = async (
  desc: TTexture,
  manager: LoadingManager,
  renderer: WebGLRenderer,
  tags?: string[],
) => {
  // Enable premultiplied alpha texture load
  const usePremultipliedAlpha =
    FlagsModule.isEnabled(FeatFlag.PREMULTIPLIED_ALPHA_LOGOS) && tags?.includes('LOGO');

  // DEBUG: Do not transform already premultiplied images
  // const usePremultipliedAlpha =
  //   FlagsModule.isEnabled(FeatFlag.PREMULTIPLIED_ALPHA_LOGOS) &&
  //   tags?.includes('LOGO') &&
  //   !desc.href.includes('pma.png');

  const texture = await loadTexure(desc, manager, renderer, {
    premultiplyAlpha: usePremultipliedAlpha ? 'premultiply' : 'none',
  });

  // Enable custom mipmaps
  if (tags?.includes('FABRIC')) {
    texture.generateMipmaps = false;
    generateCustomMipMaps(texture, tags);
  }

  return texture;
};

const loadTextureRecolorMipMaps = async (
  desc: TTexture,
  manager: LoadingManager,
  renderer: WebGLRenderer,
  color: IVector3,
  recolorContext: RecolorContext,
  tags?: string[],
) => {
  // apply recolor
  const id = desc.id;
  await recolorContext.loadEx(id, desc.href, color);
  const recolor = recolorContext.getEx(id, color)!;
  // load with recolored blob
  const descMod = produce(desc, (desc) => {
    desc.href = recolor.blobUrl;
  });
  return loadTextureMipMaps(descMod, manager, renderer, tags);
};
